import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { downloadFile } from '../../../../functions/download-file';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { BillEorCodesResponse } from '../../../../models/api/view/claim/financials/bills/bill-eor-codes-response';
import { BillEorFooterResponse } from '../../../../models/api/view/claim/financials/bills/bill-eor-footer-response';
import { BillEorHeaderResponse } from '../../../../models/api/view/claim/financials/bills/bill-eor-header-response';
import { BillItem } from '../../../../models/api/view/claim/financials/bills/bill-item';
import { BillLinesRequest } from '../../../../models/api/view/claim/financials/bills/bill-lines-request';
import { BillLinesResponse } from '../../../../models/api/view/claim/financials/bills/bill-lines-response';
import { ClaimBillResponse } from '../../../../models/api/view/claim/financials/bills/claim-bill-response';
import { EorItem } from '../../../../models/api/view/claim/financials/bills/eor-item';
import { EorPrintRequest } from '../../../../models/api/view/claim/financials/bills/eor-print-request';
import { EorPrintResponse } from '../../../../models/api/view/claim/financials/bills/eor-print-response';
import {
  Payment,
  formatAddress,
  formatPaymentType,
  formatTaxId,
} from '../../../../models/api/view/claim/financials/payment-history/payment';
import { Api } from '../../../../services/api/api.service';
import { BaseService } from '../../../../services/base.service';
import { ExportService } from '../../../../services/export/export.service';
import { BillDataTransformer } from '../../../../utilities/bill-data-transformer';
import { InvoiceLineExportMapper } from '../../../../utilities/invoice-line-export-mapper';
import { ExportFormat } from '../../../core/buttons/export-button.component';

@Injectable()
export class InvoiceBillService extends BaseService {
  private api = inject(Api);
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  /**
   * Fetches detailed information for a specific bill by ICN
   */
  async getClaimBill(
    claimKey: ClaimKey,
    icn: string,
  ): Promise<ClaimBillResponse | null> {
    return await this.api.bills.getClaimBill(claimKey, icn);
  }

  /**
   * Fetches payment data for a bill
   */
  async getPaymentData(
    claimKey: ClaimKey,
    paymentUid: string,
  ): Promise<Payment | null> {
    // First, get allocations for the claim
    const allocations = await this.api.paymentHistory.getAllocation(
      claimKey.recordSource,
      claimKey.recordId,
    );

    // Filter allocations to find the one(s) matching this specific payment
    const matchingAllocations =
      allocations?.filter(
        (allocation) => allocation.paymentuid === paymentUid,
      ) || [];

    if (matchingAllocations.length > 0) {
      const allocateId = matchingAllocations[0].allocateUid;

      // Call the getPayment API with the correct paymentUid and matching allocateId
      return await this.api.paymentHistory.getPayment(
        claimKey.recordSource,
        claimKey.recordId,
        allocateId,
      );
    } else {
      // No matching allocation found - use getPaymentById with null allocateId
      const paymentIdNum = parseFloat(paymentUid);
      const payment = await this.api.paymentHistory.getPaymentById(
        claimKey.recordSource,
        claimKey.recordId,
        paymentIdNum,
        null,
      );

      // Populate concatenated fields not returned by getPaymentById
      if (payment) {
        payment.paymentType = formatPaymentType(payment);
        payment.address = formatAddress(payment);
        payment.taxId = formatTaxId(payment);
      }

      return payment;
    }
  }

  /**
   * Fetches bill lines for a bill
   */
  async getBillLines(
    claimKey: ClaimKey,
    billUid: string,
  ): Promise<BillLinesResponse | null> {
    // Prepare the request with pagination and sorting parameters
    const billLinesRequest: BillLinesRequest = {
      search: false,
      rows: 10,
      page: 1,
      sidx: 'lineNum',
      sord: 'asc',
      showAll: true,
    };

    // Call the API
    const response = await this.api.bills.getBillLines(
      claimKey,
      billUid,
      billLinesRequest,
    );

    // Transform bill lines to ensure lineNum is numeric for proper sorting
    if (response) {
      const lines = BillDataTransformer.getBillLines(response);
      const transformedLines = BillDataTransformer.transformBillLines(lines);

      // Update the response with transformed lines (handle both property name cases)
      if ('billLines' in response) {
        (response as any).billLines = transformedLines;
      } else if ('BillLines' in response) {
        (response as any).BillLines = transformedLines;
      }
    }

    return response;
  }

  /**
   * Fetches all EOR data for a bill (header, footer, and codes)
   */
  async getEorData(
    claimKey: ClaimKey,
    billUid: string | number,
  ): Promise<{
    header: BillEorHeaderResponse | null;
    footer: BillEorFooterResponse | null;
    codes: BillEorCodesResponse | null;
  }> {
    // Pass billUid as-is to preserve decimal values like "51185488.904"
    // The backend expects the full identifier including decimal portion

    // Fetch all three EOR endpoints in parallel
    const [header, footer, codes] = await Promise.all([
      this.api.bills.getBillEorHeader(claimKey, billUid),
      this.api.bills.getBillEorFooter(claimKey, billUid),
      this.api.bills.getBillEorCodes(claimKey, billUid),
    ]);

    return { header, footer, codes };
  }

  /**
   * Request EOR print for selected bills
   * @param claimKey The claim key containing recordSource and recordId
   * @param bills Array of BillItem objects to print EORs for
   * @returns Promise of EorPrintResponse
   */
  async requestEorPrint(
    claimKey: ClaimKey,
    bills: BillItem[],
  ): Promise<EorPrintResponse> {
    // Convert BillItem array to EorItem array
    const eorItems: EorItem[] = bills.map((bill) => {
      const contractNumber = parseInt(bill.contractNumber, 10);

      if (isNaN(contractNumber)) {
        throw new Error(
          `Invalid contract number for bill ${bill.icn}: "${bill.contractNumber}" is not a valid number`,
        );
      }

      return {
        ICN: bill.icn,
        ContractNumber: contractNumber,
        VendorID: bill.vendorId,
      };
    });

    const request: EorPrintRequest = {
      RecordSource: claimKey.recordSource,
      RecordId: String(claimKey.recordId),
      EorItems: eorItems,
    };

    return await this.api.bills.requestEorPrint(claimKey, request);
  }

  /**
   * Download EOR file using download token
   * @param downloadToken The encrypted token from EorPrintResponse
   * @param filename Optional filename for the downloaded file
   */
  async downloadEor(downloadToken: string, filename?: string): Promise<void> {
    const blob = await this.api.bills.downloadEor(downloadToken);

    // Determine file type and default filename
    const contentType = blob.type;
    const isZip =
      contentType === 'application/zip' ||
      contentType === 'application/x-zip-compressed';
    let finalFilename = filename;
    if (!finalFilename) {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      finalFilename = isZip ? `EORs_${timestamp}.zip` : `EOR_${timestamp}.pdf`;
    }

    const mimeType = isZip ? 'application/zip' : 'application/pdf';
    downloadFile(blob, finalFilename, mimeType);
  }

  /**
   * Validate bills for EOR eligibility
   * @param bills Array of BillItem objects to validate
   * @param maxEorCount Maximum number of EORs allowed per request (default: 70)
   * @returns Validation result with valid flag and optional error message
   */
  validateEorEligibility(
    bills: BillItem[],
    maxEorCount = 70,
  ): { valid: boolean; message?: string } {
    if (bills.length === 0) {
      return {
        valid: false,
        message: 'Please select at least one bill to request EOR reprint.',
      };
    }

    if (bills.length > maxEorCount) {
      return {
        valid: false,
        message: `The maximum EORs that can be processed at once is ${maxEorCount}.`,
      };
    }

    // Check if bills have required fields
    const invalidBills = bills.filter(
      (bill) => !bill.icn || !bill.contractNumber || !bill.vendorId,
    );

    if (invalidBills.length > 0) {
      return {
        valid: false,
        message:
          'Some selected bills are missing required information (ICN, Contract Number, or Vendor ID).',
      };
    }

    return { valid: true };
  }

  /**
   * Validate a single bill for EOR eligibility
   * @param bill BillItem object to validate
   * @returns Validation result with valid flag and optional error message
   */
  validateSingleBillEligibility(bill: BillItem | null): {
    valid: boolean;
    message?: string;
  } {
    if (!bill) {
      return {
        valid: false,
        message: 'No bill selected',
      };
    }

    if (!bill.icn) {
      return {
        valid: false,
        message: 'Bill is missing ICN (Invoice Control Number)',
      };
    }

    if (!bill.contractNumber) {
      return {
        valid: false,
        message: 'Bill is missing Contract Number',
      };
    }

    if (!bill.vendorId) {
      return {
        valid: false,
        message: 'Bill is missing Vendor ID',
      };
    }

    return { valid: true };
  }

  /**
   * Process EOR reprint request with automatic download handling
   * This orchestrates the full workflow: request -> validate response -> download
   *
   * @param claimKey The claim key containing recordSource and recordId
   * @param bills Array of BillItem objects to print EORs for
   * @param customFilename Optional custom filename for single bill downloads
   * @returns Promise with success flag, optional error message, and response metadata
   */
  async processEorReprint(
    claimKey: ClaimKey,
    bills: BillItem[],
    customFilename?: string,
  ): Promise<{
    success: boolean;
    error?: string;
    successCount?: number;
    failureCount?: number;
    totalCount?: number;
    partialSuccess?: boolean;
  }> {
    // Request EOR print
    const response = await this.requestEorPrint(claimKey, bills);

    // Handle both camelCase and PascalCase from backend
    const responseAny = response as any;
    const hasDownloadableFile =
      response.hasDownloadableFile ?? responseAny.HasDownloadableFile ?? false;
    const downloadToken = response.downloadToken || responseAny.DownloadToken;
    const failureCount = response.failureCount ?? responseAny.FailureCount ?? 0;
    const successCount = response.successCount ?? responseAny.SuccessCount ?? 0;
    const totalCount = response.totalCount ?? responseAny.TotalCount ?? 0;
    const errorMessage = response.error || responseAny.Error;

    // Case 1: Complete failure - no downloadable file
    if (!hasDownloadableFile) {
      return {
        success: false,
        error: errorMessage || 'No downloadable file available',
        failureCount,
        successCount,
        totalCount,
      };
    }

    // Case 2: No download token despite having downloadable file
    if (!downloadToken) {
      return {
        success: false,
        error: 'Download token missing.',
        failureCount,
        successCount,
        totalCount,
      };
    }

    // Case 3: Success or partial success - download the file
    await this.downloadEor(downloadToken, customFilename);

    return {
      success: true,
      successCount,
      failureCount,
      totalCount,
      partialSuccess: failureCount > 0,
      error: failureCount > 0 ? errorMessage : undefined,
    };
  }

  /**
   * Export bill lines to CSV or Excel format
   * @param billLinesData Bill lines response data
   * @param claimInfo Claim information for enrichment
   * @param billInfo Bill information for enrichment
   * @param format Export format (csv or xlsx)
   * @param filenamePrefix Prefix for the generated filename
   */
  async exportBillLines(
    billLinesData: any,
    claimInfo: {
      claimNumber?: string;
      fileNumber?: string;
      claimantFirstName?: string;
      claimantLastName?: string;
      accountName?: string;
    },
    billInfo: {
      contractNumber?: string;
      contractName?: string;
      icn?: string;
      invoice?: string;
    },
    format: ExportFormat,
    filenamePrefix: string,
  ): Promise<void> {
    const billLines = BillDataTransformer.getBillLines(billLinesData);
    const enrichedLines = BillDataTransformer.enrichBillLinesForExport(
      billLines,
      claimInfo,
      billInfo,
    );

    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `${filenamePrefix}_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = InvoiceLineExportMapper.toCsvExport(
        enrichedLines,
        'Bill Line Items',
      );
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = InvoiceLineExportMapper.toExcelExport(
        enrichedLines,
        'Bill Line Items',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }
}
